// Generated by DesignToCode

import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  computed,
  input,
  signal,
  effect,
  output,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-search-field',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SvgIconComponent],
  templateUrl: './search-field.html',
})
export class SearchField implements OnInit, OnDestroy {
  public inputContainerRef = viewChild<ElementRef>('inputContainer');
  public inputRef = viewChild<ElementRef>('input');

  // Inputs
  public size = input<'small' | 'medium' | 'large'>('medium');
  public formGroup = input.required<FormGroup>();
  public controlName = input.required<string>();
  public placeholder = input<string>('');
  public helper = input<string>('');
  // Inputs for states
  public disabled = input<boolean>(false);
  public readonly = input<boolean>(false);
  // Inputs for Validations
  public showValidationTemplates = input<boolean>(false);
  public errorMessage = input<string>('Error text');
  public successMessage = input<string>('Success text');

  // When it is used for dropdown
  public isDropdown = input<boolean>(false);
  public dropdownExpanded = input<boolean>(false);
  public dropdownSelectedValue = input<unknown>();
  public currentInputValue = signal<unknown>(null);

  // Output
  public onClickRightButton = output<boolean>();

  public leftIcon = signal<string>('icon_search_20px.svg');
  public rightIcon = signal<string>('icon_cancel_16px.svg');

  // States
  private isHovered = signal(false);
  private isFilling = signal(false);
  private isFocused = signal(false);
  private isFilled = signal(false);

  private destroy$ = new Subject<void>();

  private formState = signal({
    invalid: false,
    dirty: false,
    touched: false,
    valid: false,
    disabled: false,
  });

  constructor() {
    effect(() => {
      const fc = this.formGroup()?.get(this.controlName());
      if (fc?.value != null && fc?.value !== '') {
        this.isFilled.set(true);
      }
    });

    effect(() => {
      if (this.isDropdown()) {
        if (this.currentInputValue() === null) {
          this.isFilling.set(false);
          this.isFilled.set(false);
        } else {
          if (this.currentInputValue() === this.dropdownSelectedValue()) {
            this.isFilling.set(false);
            this.isFilled.set(true);
          } else {
            this.isFilling.set(true);
            this.isFilled.set(false);
          }
        }

        if (this.dropdownExpanded()) {
          this.inputRef()?.nativeElement.focus();
        }
      }
    });
  }

  ngOnInit(): void {
    if (this.control() != null) {
      this.control()
        .statusChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (
            this.formState().dirty !== this.control().dirty ||
            this.formState().invalid !== this.control().invalid ||
            this.formState().touched !== this.control().touched ||
            this.formState().valid !== this.control().valid ||
            this.formState().disabled !== this.control().disabled
          ) {
            this.formState.set({
              invalid: this.control().invalid,
              dirty: this.control().dirty,
              touched: this.control().touched,
              valid: this.control().valid,
              disabled: this.control().disabled,
            });
          }
        });

      this.control().updateValueAndValidity();
    }

    this.control()
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.currentInputValue.set(value);
        if (value != null) {
          this.isFocused.set(false);
          this.isFilling.set(true);
        }
        if (this.isDropdown()) {
          if (this.currentInputValue() === null) {
            this.isFilling.set(false);
            this.isFilled.set(false);

            if (this.dropdownSelectedValue() === null) {
              this.isFocused.set(false);
            } else {
              this.isFocused.set(true);
            }
          } else {
            if (this.currentInputValue() === this.dropdownSelectedValue()) {
              this.isFilling.set(false);
              this.isFilled.set(true);
            } else {
              this.isFilling.set(true);
              this.isFilled.set(false);
            }
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public control = computed(() => {
    const fc = this.formGroup()?.get(this.controlName());
    return fc as FormControl;
  });

  // Get error or helper message
  public message = computed(() => {
    return this.helper();
  });

  public onMouseEnter() {
    this.isHovered.set(true);
  }

  public onMouseLeave() {
    this.isHovered.set(false);
  }

  public onFocus() {
    this.isFocused.set(true);
    this.isFilled.set(false);
  }

  public onBlur() {
    this.isFocused.set(false);
    this.isFilling.set(false);
    this.isFilled.set(
      this.control().value != null && this.control().value !== '',
    );
    this.formState.set({
      invalid: this.control().invalid,
      dirty: this.control().dirty,
      touched: this.control().touched,
      valid: this.control().valid,
      disabled: this.control().disabled,
    });
  }

  public onClickElement() {
    if (this.isDropdown()) {
      this.isFocused.set(true);
    }
  }

  public tabIndexInput = computed(() => {
    return this.disabled() || this.readonly() ? -1 : 0;
  });

  public isDisabled = computed(() => this.disabled() || this.formState().disabled);

  public tabIndexRightIconButton = computed(() => {
    return this.isDisabled() || this.readonly() ? -1 : 0;
  });

  public onClickRightIcon() {
    this.control().setValue('');
    this.isFilled.set(false);
    this.onClickRightButton.emit(true);
  }

  public containerClasses = computed(() => {
    const classes: string[] = [
      'flex',
      'items-center',
      'gap-xs',
      'w-full',
      'transition-all',
      'duration-200',
    ];

    if (this.size() === 'small') {
      classes.push('rounded-2xs', 'px-3xs', 'py-3xs');
    } else if (this.size() === 'medium') {
      classes.push('rounded-xs', 'px-sm', 'py-xs');
    } else if (this.size() === 'large') {
      classes.push('rounded-sm', 'px-sm', 'py-sm');
    }

    if (this.isFilling() && !this.disabled()) {
      classes.push(
        'bg-navy-blue-20',
        'border',
        'border-navy-blue-100',
        'text-grey-120',
      );
    } else if (this.isFocused() && !this.disabled()) {
      classes.push(
        'bg-navy-blue-20',
        'border',
        'border-navy-blue-100',
        'text-grey-120',
        'shadow-[0px_0px_0px_3px_rgba(208,221,255,1)]',
      );
    } else if (this.isHovered() && !this.disabled()) {
      classes.push('bg-grey-20', 'border', 'border-grey-90', 'text-grey-90');
    } else if (this.isFilled() && !this.disabled()) {
      classes.push('bg-grey-20', 'border', 'border-grey-100', 'text-grey-120');
    } else if (this.disabled()) {
      classes.push(
        'bg-grey-20',
        'border',
        'border-grey-50',
        'text-grey-80',
        'cursor-not-allowed',
      );
    } else {
      classes.push('bg-grey-20', 'border', 'border-grey-50', 'text-grey-90');
    }

    return classes.join(' ');
  });

  public leftIconClasses = computed(() => {
    const classes: string[] = ['w-xl', 'h-xl'];

    if (this.isFilling() && !this.disabled()) {
      classes.push('fill-grey-120');
    } else if (this.isFocused() && !this.disabled()) {
      classes.push('fill-grey-120');
    } else if (this.isHovered() && !this.disabled()) {
      classes.push('fill-grey-100');
    } else if (this.isFilled() && !this.disabled()) {
      classes.push('fill-grey-120');
    } else if (this.disabled()) {
      classes.push('fill-grey-80', 'cursor-not-allowed');
    } else {
      classes.push('fill-grey-100');
    }

    return classes.join(' ');
  });

  public inputClasses = computed(() => {
    const classes: string[] = [
      'bg-transparent',
      'w-full',
      'outline-none',
      'font-body',
      'font-medium',
    ];

    if (this.size() === 'small') {
      classes.push('text-sm/lg');
    } else {
      classes.push('text-md/xl');
    }

    if (this.isFilling() && !this.disabled()) {
      classes.push('text-grey-120', 'placeholder:text-grey-90');
    } else if (this.isFocused() && !this.disabled()) {
      classes.push('text-grey-120', 'placeholder:text-grey-90');
    } else if (this.isHovered() && !this.disabled()) {
      classes.push('text-grey-100', 'placeholder:text-grey-90');
    } else if (this.isFilled() && !this.disabled()) {
      classes.push('text-grey-120', 'placeholder:text-grey-90');
    } else if (this.disabled()) {
      classes.push(
        'text-grey-80',
        'placeholder:text-grey-80',
        'cursor-not-allowed',
      );
    } else {
      classes.push('text-grey-100', 'placeholder:text-grey-90');
    }

    if (this.readonly() && !this.disabled()) {
      classes.push('cursor-default');
    }

    return classes.join(' ');
  });

  public rightIconClasses = computed(() => {
    const classes: string[] = [
      'transition-transform',
      'duration-200',
      'w-lg',
      'h-lg',
    ];

    if (this.disabled()) {
      classes.push('fill-grey-80', 'cursor-not-allowed');
    } else {
      classes.push('fill-grey-120', 'cursor-pointer');
    }

    return classes.join(' ');
  });

  public messageClasses = computed(() => {
    const classes = [
      'font-body',
      'font-medium',
      'text-xs',
      'pt-3xs',
      'text-grey-100',
    ];

    if (this.size() === 'small') {
      classes.push('px-3xs');
    } else {
      classes.push('px-sm');
    }

    return classes.join(' ');
  });
}
